import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {AbstractComponent} from "../abstract.component";
import {NgbDropdown, NgbOffcanvas, NgbOffcanvasRef} from "@ng-bootstrap/ng-bootstrap";
import {FilterGroupType} from "../filter-panel/filter-panel.component";


@Component({
  selector: 'app-filter-item',
  templateUrl: './filter-item.component.html',
  styleUrls: ['./filter-item.component.scss'],
  standalone: false
})
export class FilterItemComponent extends AbstractComponent {

  @Input() title: string = "";
  @Input() items: FilterItem[] = [];
  @Input() id: string = "";
  @Input() allowSearchItems: boolean = false;
  @Input() type: FilterGroupType = FilterGroupType.SINGLE_SELECT;
  @Input() requiredAny: boolean = false;
  @Input() showSelectionAsLabel: boolean = false;

  @Output() onChange = new EventEmitter<FilterItem[]>();

  @ViewChild('desktopDropdown', { static: false }) desktopDropdown?: NgbDropdown;
  @ViewChild('offcanvasContent') offcanvasContent?: TemplateRef<any>;

  localStateItems: FilterItem[] = [];
  filteredItems: FilterItem[] = [];
  searchText?: string;

  private offcanvasRef?: NgbOffcanvasRef;

  constructor(private offcanvasService: NgbOffcanvas) {
    super();
  }

  ngOnInit() {
    this.initLocalStateItems();
    this.subscriptions.push(this.isMobileSubject.subscribe(_ => {
      this.onFilterClose();
    }))
  }

  initLocalStateItems() {
    if (this.type === FilterGroupType.SINGLE_SELECT_BUTTONS) {
      this.localStateItems = this.items.map(f => ({ ...f }));
      this.filteredItems = [...this.localStateItems];
    }
  }


  @HostListener('window:scroll')
  onScroll() {
    if (this.isBrowser) {
      this.desktopDropdown?.close();
    }
  }

  handleInput(input: string): void {
    const trimmed = input?.trim().toLowerCase() || '';
    this.searchText = trimmed;
    this.setFilteredItems();
  }

  setFilteredItems() {
    if (this.searchText && this.allowSearchItems) {
      this.filteredItems = [...this.localStateItems.filter(i => i.label?.toLowerCase().includes(this.searchText!)
      )];
    }
    else {
      this.filteredItems = [...this.localStateItems];
    }
  }

  toggleItem(event: Event, item: FilterItem) {
    const checkbox = event.target as HTMLInputElement;

    // Early return: prevent deselecting the last required item
    if (this.requiredAny && item.selected && this.localStateItems.filter(f => f.selected).length === 1) {
      checkbox.checked = true;
      return;
    }

    // Update selection state
    if (this.type === FilterGroupType.MULTI_SELECT) {
      item.selected = !item.selected;
    } else {
      // Single select: deselect others
      this.localStateItems.forEach(f => f.selected = f.id === item.id ? !item.selected : false);
    }

    this.setFilteredItems();

    // Emit changes for specific scenarios
    const selected = this.localStateItems.filter(f => f.selected);
    const shouldEmit = this.isDesktop || this.type === FilterGroupType.SINGLE_SELECT_BUTTONS;

    if (shouldEmit) {
      this.onChange.emit(selected);
      if (this.type === FilterGroupType.SINGLE_SELECT) {
        this.onClose();
      }
    }
  }

  onOpenMobileFilter() {
    this.localStateItems = this.items.map(f => ({ ...f }));
    this.searchText = undefined;
    this.setFilteredItems();

    if (this.offcanvasContent) {
      this.offcanvasRef = this.offcanvasService.open(this.offcanvasContent, {
        position: 'bottom',
        panelClass: 'mobile-filter-sheet',
        backdropClass: 'offcanvas-backdrop'
      });
    }
  }

  onDesktopDropdownOpen(isOpen: boolean) {
    if (isOpen) {
      this.localStateItems = this.items.map(f => ({ ...f }));
      this.searchText = undefined;
      this.setFilteredItems();
    }
  }

  onApply() {
    const selected = this.localStateItems.filter(item => item.selected);
    this.onChange.emit(selected);
    this.onFilterClose();
  }

  onClear() {
    this.onChange.emit([]);
    this.onFilterClose();
  }

  onFilterClose(): void {
    this.desktopDropdown?.close();
    this.offcanvasRef?.close();
  }

  onClose() {
    this.onFilterClose();
  }

  protected readonly FilterGroupType = FilterGroupType;
}

export class FilterItem {
  constructor(public id?: number,
              public label?: string,
              public labelKey?: string,
              public selected?: boolean,
              public isDefault: boolean = false) {}
}